import type React from "react"
import { Link } from "react-router-dom"
import Navbar from "../../components/Navbar"
import Footer from "../../components/Footer"
import { CheckCircle, Snowflake, Thermometer, Wind, Clock } from "lucide-react"

const WinterApplicationsPage: React.FC = () => {
  const applications = [
    {
      title: "Snow Plowing & Removal",
      description: "Efficient snow removal for roads, parking lots, and pathways",
      image: "https://placehold.co/600x400?text=Snow+Plowing",
      attachments: ["Snow Plough", "V Snow Plough", "Box Plough", "Combi Plough & Brush"],
    },
    {
      title: "De-icing & Salt Spreading",
      description: "Prevent ice formation and maintain safe driving conditions",
      image: "https://placehold.co/600x400?text=De-icing",
      attachments: ["Salt Spreader", "De-icing Sprayer", "Drop Sander", "Liquid Applicator"],
    },
    {
      title: "Airport Winter Operations",
      description: "Specialized winter maintenance for runways and taxiways",
      image: "https://placehold.co/600x400?text=Airport+Winter",
      attachments: ["Snow Blower", "Glycol Recovery", "Runway Sweeper", "Ice Breaker"],
    },
    {
      title: "Sidewalk & Pathway Clearing",
      description: "Precise snow and ice removal for pedestrian areas",
      image: "https://placehold.co/600x400?text=Sidewalk+Clearing",
      attachments: ["Snow Brush", "Compact Plough", "Sidewalk Sweeper", "Mini Salt Spreader"],
    },
  ]

  const attachments = [
    {
      name: "Snow Plough",
      description: "Efficient snow removal for roads and paths",
      specs: "Multiple blade configurations available",
    },
    {
      name: "V Snow Plough",
      description: "V-shaped plough for heavy snow conditions",
      specs: "Superior snow throwing capability",
    },
    {
      name: "Box Plough",
      description: "Containment plough for controlled snow removal",
      specs: "Prevents snow spillback",
    },
    {
      name: "Combi Box Plough & Brush",
      description: "Combined ploughing and brushing",
      specs: "Two operations in one pass",
    },
    {
      name: "Snow Brush",
      description: "Gentle snow removal for sensitive surfaces",
      specs: "Adjustable brush pressure",
    },
    {
      name: "Snow Blower",
      description: "High-capacity snow throwing",
      specs: "Throws snow up to 30 meters",
    },
    {
      name: "Salt Spreader",
      description: "Precise salt and grit distribution",
      specs: "Variable spread width and rate",
    },
    {
      name: "De-Icing Sprayer",
      description: "Liquid de-icing application",
      specs: "Even application coverage",
    },
  ]

  const benefits = [
    {
      icon: Snowflake,
      title: "All-Weather Performance",
      description: "Reliable operation in the harshest winter conditions",
    },
    {
      icon: Clock,
      title: "Rapid Response",
      description: "Quick deployment for emergency snow and ice situations",
    },
    {
      icon: Thermometer,
      title: "Temperature Adaptability",
      description: "Effective performance across all winter temperature ranges",
    },
    {
      icon: Wind,
      title: "Efficient Coverage",
      description: "Wide coverage patterns for maximum efficiency",
    },
  ]

  const winterStats = [
    { number: "24/7", label: "Winter Readiness" },
    { number: "-30°C", label: "Operating Temperature" },
    { number: "30m", label: "Snow Throw Distance" },
    { number: "100%", label: "Uptime Reliability" },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cyan-900 via-blue-900 to-cyan-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              Winter Operations
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Complete
              <br />
              <span className="text-cyan-400">Winter Solutions</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Professional winter maintenance solutions for snow removal, de-icing, and cold weather operations. Keep
              roads, runways, and pathways safe and accessible all winter long.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Get Winter Solutions Quote
              </Link>
              <Link
                to="/attachments"
                className="border border-white text-white hover:bg-white hover:text-cyan-900 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                View Winter Attachments
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Winter Stats */}
      <section className="py-16 bg-cyan-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {winterStats.map((stat, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="text-3xl font-bold text-cyan-600 mb-2">{stat.number}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Winter Applications</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive winter maintenance solutions designed to handle the most challenging snow and ice
                conditions across all environments.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {applications.map((app, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <img src={app.image || "/placeholder.svg"} alt={app.title} className="w-full h-64 object-cover" />
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{app.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{app.description}</p>
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Recommended Attachments:</h4>
                      <div className="flex flex-wrap gap-2">
                        {app.attachments.map((attachment, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm"
                          >
                            {attachment}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Multihog for Winter Operations?</h2>
              <p className="text-lg text-gray-600">
                Experience the advantages of professional winter maintenance equipment
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="bg-cyan-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <benefit.icon className="h-8 w-8 text-cyan-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Attachments Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Winter Attachments</h2>
              <p className="text-lg text-gray-600">
                Specialized attachments designed for professional winter maintenance operations
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {attachments.map((attachment, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-3">
                    <CheckCircle className="h-5 w-5 text-cyan-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">{attachment.name}</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{attachment.description}</p>
                  <p className="text-cyan-600 text-xs font-medium">{attachment.specs}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Response Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-100 to-blue-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">24/7 Winter Emergency Response</h2>
            <p className="text-lg text-gray-600 mb-8">
              When winter weather strikes, you need equipment that's ready to respond immediately. Multihog tractors are
              designed for rapid deployment and reliable performance in emergency situations.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Rapid Deployment</h3>
                <p className="text-gray-600">Quick attachment changes for immediate response to changing conditions</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Reliable Performance</h3>
                <p className="text-gray-600">Proven reliability in extreme weather conditions and temperatures</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Operator Comfort</h3>
                <p className="text-gray-600">Heated cabs and ergonomic controls for long winter operations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready for Winter?</h2>
            <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
              Don't let winter weather catch you unprepared. Equip your team with professional-grade Multihog winter
              solutions and ensure safe, accessible roads and pathways all season long.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-cyan-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Get Winter Readiness Quote
              </Link>
              <Link
                to="/models"
                className="border border-white text-white hover:bg-white hover:text-cyan-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                View Winter-Ready Models
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default WinterApplicationsPage
